import React, { useContext, useEffect, useRef, useState } from "react";
import { langContext } from "../../App";
import { useStore } from "../../store";
import { langGoogle } from "./googleTranslate";
import CustomModal from "../modal/CustomModal";
import CountryCodeSelect from "../CountryCodeSelect";
import { isKnownDialCode } from "../../const/countryCodes";
import AlertComponent from "../../utils/Alert";
import { ALERT_MESSAGE_TYPE_DANGER } from "../../const/Constants";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID
const SCRIPT_SRC = "https://accounts.google.com/gsi/client"
const LOCALES = { arm: "hy", eng: "en", fr: "fr", arb: "ar", rus: "ru" }

const GoogleLogo = () => (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
)

let scriptPromise = null
const loadGoogleScript = () => {
    if (window.google?.accounts?.id) return Promise.resolve()
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script")
            script.src = SCRIPT_SRC
            script.async = true
            script.onload = resolve
            script.onerror = () => { scriptPromise = null; reject(new Error("Google script failed to load")) }
            document.head.appendChild(script)
        })
    }
    return scriptPromise
}

// "Sign in with Google" for the login and register pages. New users are asked for a
// phone number (registration requires one) before the account is created.
// remember: keep the session until manual logout (the register page always does)
const GoogleSignIn = ({ remember = true }) => {
    const lang = useContext(langContext)
    const t = langGoogle[lang] || langGoogle.arm
    const { userStore } = useStore()
    // Google's OAuth token client, created once the Google script has loaded
    const tokenClientRef = useRef(null)
    // read by the Google popup callback, which is created once and would otherwise keep the first value
    const rememberRef = useRef(remember)
    rememberRef.current = remember
    const [ready, setReady] = useState(false)

    // set when Google is fine but this is a new account that still needs a phone number
    const [pending, setPending] = useState(null)
    const [phone, setPhone] = useState({ code: "374", number: "" })
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const phoneInputRef = useRef(null)

    const errorText = (result) => t.errors[result?.payload?.code] || result?.message || t.errors.GOOGLE_INVALID

    const finishLogin = async () => {
        const currentUser = await userStore.getCurrentUser()
        if (!currentUser) return
        userStore.setUsername(currentUser.username)
        localStorage.setItem("user", currentUser.username)
        window.location = "/home"
    }

    const signIn = async (accessToken, tel_number) => {
        setLoading(true)
        const result = await userStore.googleLogin({ access_token: accessToken, tel_number, remember: rememberRef.current })
        setLoading(false)
        if (result?.status) {
            setPending(null)
            await finishLogin()
            return
        }
        if (result?.payload?.code === "GOOGLE_NEEDS_PHONE") {
            setPending({ accessToken, ...result.payload })
            setError("")
            return
        }
        if (pending) setError(errorText(result))
        else AlertComponent(ALERT_MESSAGE_TYPE_DANGER, errorText(result))
    }

    // Our own button (Google's rendered one follows the Google account's language, not the app's)
    useEffect(() => {
        if (!CLIENT_ID) return
        let cancelled = false
        loadGoogleScript().then(() => {
            if (cancelled) return
            tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
                client_id: CLIENT_ID,
                scope: "openid email profile",
                callback: (response) => {
                    if (response.error || !response.access_token) {
                        AlertComponent(ALERT_MESSAGE_TYPE_DANGER, t.errors.GOOGLE_INVALID)
                        return
                    }
                    signIn(response.access_token)
                },
            })
            setReady(true)
        }).catch(() => {})
        return () => { cancelled = true }
    }, [])

    const openGooglePopup = () => {
        // must run straight from the click, otherwise browsers block the popup
        tokenClientRef.current?.requestAccessToken({ prompt: "select_account", hl: LOCALES[lang] || "en" })
    }

    const submitPhone = () => {
        if (!isKnownDialCode(phone.code)) {
            setError(t.tr_selectCode)
            return
        }
        const digits = phone.code + phone.number.replace(/\D/g, "")
        if (digits.length < 8 || digits.length > 15) {
            setError(t.errors.PHONE_INVALID)
            return
        }
        signIn(pending.accessToken, `+${digits}`)
    }

    // the button only exists once a Google client id is configured
    if (!CLIENT_ID) return null

    return (
        <>
            <div className="info-center" style={{ alignItems: "center", gap: 12, margin: "4px 0 8px" }}>
                <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.35)", maxWidth: 200 }} />
                <span className="fontstyle" style={{ fontSize: 13, opacity: 0.85 }}>{t.tr_or}</span>
                <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.35)", maxWidth: 200 }} />
            </div>
            <div className="info-center">
                <button type="button" onClick={openGooglePopup} disabled={!ready || loading}
                        style={{ background: "#FFFFFF", color: "#1F1F1F", border: "1px solid #747775", marginTop: 0, gap: 10,
                                 fontFamily: "Roboto, Arial, sans-serif", fontWeight: 500, fontSize: 14, cursor: "pointer" }}>
                    <GoogleLogo />
                    <span>{t.tr_continueWithGoogle}</span>
                </button>
            </div>

            <CustomModal isOpen={!!pending} isClose={() => setPending(null)}>
                {pending && (
                    <div style={{ width: "min(420px, calc(100vw - 64px))", color: "#2C507B" }}>
                        <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{t.tr_finishTitle}</p>
                        <p style={{ fontSize: 14, marginBottom: 16 }}>{t.tr_finishText(pending.name || pending.email)}</p>
                        <p style={{ fontSize: 14, marginBottom: 4 }}>{t.tr_phoneNumber}</p>
                        <div className="info-input-div" style={{ position: "relative", left: 0, top: 0, width: "100%" }}>
                            <CountryCodeSelect lang={lang} value={phone.code}
                                onChange={(code) => { setError(""); setPhone(p => ({ ...p, code })) }}
                                onSelected={() => phoneInputRef.current?.focus()} />
                            <input type="tel" ref={phoneInputRef} placeholder={t.tr_phoneNumber} value={phone.number}
                                onChange={(e) => { setError(""); setPhone(p => ({ ...p, number: e.target.value.replace(/[^\d\s()-]/g, "") })) }}
                                onKeyDown={(e) => {
                                    // the login page submits its own form on Enter anywhere in the window
                                    if (e.key !== "Enter") return
                                    e.stopPropagation()
                                    !loading && submitPhone()
                                }}
                                className="info-input" style={{ minWidth: 0, flex: 1 }} />
                        </div>
                        <p style={{ color: "tomato", fontSize: 13, minHeight: 20, marginTop: 6 }}>{error}</p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                            <button type="button" onClick={() => setPending(null)}
                                    className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">{t.tr_cancel}</button>
                            <button type="button" onClick={submitPhone} disabled={loading}
                                    className="px-4 py-2 rounded text-white disabled:opacity-60" style={{ background: "#195FFC" }}>{t.tr_continue}</button>
                        </div>
                    </div>
                )}
            </CustomModal>
        </>
    )
}

export default GoogleSignIn
